import React from 'react';

const ProcessSection = () => {
  // Process steps data
 const processSteps = [
  {
    id: 1,
    number: '01',
    title: 'Infrastructure Review',
    description: 'We evaluate your AWS environment, workloads, applications, and infrastructure requirements.',
    icon: 'chart',
    delay: '300ms'
  },
  {
    id: 2,
    number: '02',
    title: 'Migration Planning',
    description: 'A detailed migration strategy is created to ensure a smooth and efficient transition.',
    icon: 'cube',
    delay: '400ms'
  },
  {
    id: 3,
    number: '03',
    title: 'Environment Preparation',
    description: 'The target infrastructure is configured and tested before the migration begins.',
    icon: 'server',
    delay: '500ms'
  },
  {
    id: 4,
    number: '04',
    title: 'Workload Migration',
    description: 'Applications, databases, and data are migrated securely with minimal disruption.',
    icon: 'sync',
    delay: '600ms'
  },
  {
    id: 5,
    number: '05',
    title: 'Testing & Validation',
    description: 'Comprehensive testing is performed to verify functionality, performance, and stability.',
    icon: 'transfer',
    delay: '700ms'
  },
  {
    id: 6,
    number: '06',
    title: 'Go-Live & Optimization',
    description: 'The new environment is launched, optimized, and supported for long-term success.',
    icon: 'settings',
    delay: '800ms'
  }
];
  // Icon components
const ChartIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
    strokeWidth="1.5" stroke="currentColor"
    className="w-8 h-8 text-blue-500 mb-4">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M3 17l5-5 4 4 8-8" />
  </svg>
);

const CubeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
    strokeWidth="1.5" stroke="currentColor"
    className="w-8 h-8 text-blue-500 mb-4">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M12 6v6l4 2m5-2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ServerIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
    strokeWidth="1.5" stroke="currentColor"
    className="w-8 h-8 text-blue-500 mb-4">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M9 3v2m6-2v2M9 19v2m6-2v2M3 9h2m14 0h2M3 15h2m14 0h2M8 8h8v8H8V8z" />
  </svg>
);

const SyncIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
    strokeWidth="1.5" stroke="currentColor"
    className="w-8 h-8 text-blue-500 mb-4">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M16.023 9.348h4.992V4.356m-1.5 13.296H14.52v4.992M2.985 19.644l4.992-4.992m0 0L2.985 9.66m4.992 4.992h11.538" />
  </svg>
);

const TransferIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
    strokeWidth="1.5" stroke="currentColor"
    className="w-8 h-8 text-blue-500 mb-4">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M9 12.75 11.25 15 15 9.75m6 2.25a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const SettingsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
    strokeWidth="1.5" stroke="currentColor"
    className="w-8 h-8 text-blue-500 mb-4">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M10.5 6h3m-7.5 6h12m-9 6h6" />
  </svg>
);

  const getIcon = (iconType) => {
    switch (iconType) {
      case 'chart':
        return <ChartIcon />;
      case 'cube':
        return <CubeIcon />;
      case 'server':
        return <ServerIcon />;
      case 'sync':
        return <SyncIcon />;
      case 'transfer':
        return <TransferIcon />;
      case 'settings':
        return <SettingsIcon />;
      default:
        return <ChartIcon />;
    }
  };

  return (
    <section id="process-section" className="py-10 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header Section */}
        <div className="text-center mb-16">
          <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">
            Migration Process

          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mt-2 mb-4 transition-all duration-1000 opacity-100 translate-y-0">
            Your AWS Migration Journey
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto transition-all duration-1000 delay-200 opacity-100 translate-y-0">
           A structured approach designed for secure, efficient, and low-risk infrastructure migrations.
          </p>
        </div>
        
        {/* Process Steps Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {processSteps.map((step) => (
            <div 
              key={step.id}
              className="relative bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 opacity-100 translate-y-0"
              style={{ transitionDelay: step.delay, transitionDuration: '1000ms' }}
            >
              {/* Step Number Badge */}
              <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-gray-500 to-gray-500 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                {step.number}
              </div>
              
              {/* Content */}
              <div className="pt-4">
                {getIcon(step.icon)}
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-600">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
        
      </div>
    </section>
  );
};

export default ProcessSection;